// /pam/policies/:product/new and /:policyId/edit: the editor. The same page
// on every product, only Where differs:
//   header: back button, breadcrumb, "New <product> policy" / "Edit <product>
//   policy" and whether it is saved
//   left, five numbered steps: Name it · Who · Where · What should happen ·
//   Only when… (optional)
//   right, a panel that stays in view: the policy as a sentence, its
//   Who → Where → When → Then, a checklist and [Cancel] [Create policy]
// Saving goes straight to the API (no review step); the sentence says what
// will be saved.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Drawer, Input, Modal } from "antd";
import { ArrowLeft, Check, Circle } from "lucide-react";
import { useBlocker, useLocation, useNavigate, useParams } from "react-router-dom";

import { AN_COLORS } from "../../design-system/tokens";
import { PageShell } from "../../design-system/tableUi";
import { conditionClauses, conditionRows, friendlyPolicyError, GONE_SAVE_ERROR } from "../../components/policyShell/conditionsModel";
import { fixCountText, whoSentenceText } from "../../components/policyShell/policyModel";
import { signalWarning } from "../../components/policyShell/signals";
import {
  ASIDE_WIDTH,
  Eyebrow,
  Field,
  Muted,
  Note,
  PAGE_MAX,
  PageTitleBar,
  Panel,
  StatusDot,
  StepCard,
  TWO_COLUMNS,
  TextButton,
  actionMeta,
  useWide,
} from "./ui";
import {
  draftFromRow,
  fieldDomId,
  firstErrorKey,
  isAdvancedKey,
  newDraft,
  policyJsonFromDraft,
  sectionOf,
  validateDraft,
} from "./editor/draft";
import WhoSection from "./editor/WhoSection";
import ActionSection from "./editor/ActionSection";
import AdvancedBlock from "./editor/AdvancedBlock";
import { policySummarySentence } from "./editor/PolicySummary";
import { ProductGate, useAdapterData, usePolicyRow } from "./usePolicyPage";
import { policiesPath } from "./policyProducts";

export default function PolicyEditorPage() {
  const { policyId } = useParams();
  return <ProductGate render={(p) => <EditorLoader key={`${p.product}:${policyId || "new"}`} {...p} policyId={policyId} />} />;
}

export function PageMessage({ product, children }) {
  return (
    <PageShell>
      <BackLink product={product} />
      <div style={{ marginTop: "1.5rem", color: AN_COLORS.muted, fontSize: "0.875rem" }}>{children}</div>
    </PageShell>
  );
}

/** A real button back to the product's list (same look as the app pages' back buttons). */
export function BackLink({ product }) {
  const navigate = useNavigate();
  return (
    <Button onClick={() => navigate(policiesPath(product))} icon={<ArrowLeft size={14} style={{ verticalAlign: -2 }} />}>
      Policies
    </Button>
  );
}

function EditorLoader({ product, adapter, flags, policyId }) {
  const location = useLocation();
  const duplicateOf = !policyId ? location.state?.duplicateOf : null;
  const { row, loading, error } = usePolicyRow(adapter, policyId, location.state?.policy);
  if (policyId && !row) {
    if (loading) return <PageMessage product={product}>{"Loading policy…"}</PageMessage>;
    return <PageMessage product={product}>{error === "notFound" ? "This policy doesn't exist any more." : "Couldn't load this policy. Try again."}</PageMessage>;
  }
  const stored = row?.policyJson || duplicateOf?.policyJson || {};
  const initial = row ? draftFromRow(adapter, row) : duplicateOf ? draftFromRow(adapter, duplicateOf, { duplicate: true }) : newDraft(adapter);
  return (
    <PolicyEditor
      product={product}
      adapter={adapter}
      flags={flags}
      row={row}
      stored={stored}
      initialDraft={initial}
      isEdit={!!policyId}
      duplicate={!!duplicateOf && !row}
    />
  );
}

const scrollToId = (id) => {
  const el = typeof document !== "undefined" ? document.getElementById(id) : null;
  if (el && el.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "start" });
  return !!el;
};

// Scroll to an error's field, else its step.
const STEP_OF = { except: "who" };
const scrollToError = (key) => {
  const section = sectionOf(key);
  if (!scrollToId(fieldDomId(key))) scrollToId(`policy-section-${isAdvancedKey(key) && !STEP_OF[section] ? "advanced" : STEP_OF[section] || section}`);
};

/** The policy as a sentence, its verb in the action's colour. */
export function SummarySentence({ text, flow, style }) {
  const verb = actionMeta(flow).label;
  const lead = text.startsWith(verb) ? verb : "";
  return (
    <p aria-label="Policy summary" style={{ margin: 0, fontSize: "0.9375rem", color: AN_COLORS.ink2, lineHeight: 1.55, overflowWrap: "anywhere", ...style }}>
      {lead && <strong style={{ color: actionMeta(flow).fg, fontWeight: 650 }}>{lead}</strong>}
      {text.slice(lead.length)}
    </p>
  );
}

/** Who → Where → When → Then, one short line each, joined by a rail. */
export function FlowSteps({ steps }) {
  return (
    <ol style={{ listStyle: "none", margin: 0, padding: 0, display: "grid" }}>
      {steps.map((st, i) => (
        <li key={st.label} style={{ display: "grid", gridTemplateColumns: "1rem 1fr", gap: "0.75rem" }}>
          <span aria-hidden style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            <span
              style={{
                width: "0.625rem",
                height: "0.625rem",
                marginTop: "0.3125rem",
                borderRadius: "50%",
                background: st.color || (st.empty ? AN_COLORS.surface : AN_COLORS.primary),
                border: `2px solid ${st.color || (st.empty ? AN_COLORS.line : AN_COLORS.primary)}`,
                flexShrink: 0,
              }}
            />
            {i < steps.length - 1 && <span style={{ flex: 1, width: 2, background: AN_COLORS.line, margin: "0.125rem 0" }} />}
          </span>
          <span style={{ paddingBottom: i < steps.length - 1 ? "0.75rem" : 0, minWidth: 0 }}>
            <Eyebrow>{st.label}</Eyebrow>
            <span style={{ display: "block", fontSize: "0.8125rem", color: st.empty ? AN_COLORS.muted : st.color || AN_COLORS.ink, fontWeight: st.color ? 600 : 400, lineHeight: 1.5, overflowWrap: "anywhere" }}>
              {st.text}
            </span>
          </span>
        </li>
      ))}
    </ol>
  );
}

const ucFirst = (t) => (t ? t.charAt(0).toUpperCase() + t.slice(1) : t);

// "This won't apply" warnings for an Allow policy's conditions the product
// can't check (spec 3).
export function conditionWarnings(adapter, draft) {
  const keys = conditionRows(draft.conditions, { product: adapter.key })
    .filter((r) => !r.hidden)
    .map((r) => r.key);
  return [...new Set(keys.map((k) => signalWarning(adapter.key, k, draft.action)).filter(Boolean))];
}

export function PolicyEditor({ product, adapter, flags, row, stored, initialDraft, isEdit, duplicate = false }) {
  const navigate = useNavigate();
  const [modalApi, modalContext] = Modal.useModal();
  const [draft, setDraft] = useState(initialDraft);
  const [baseline, setBaseline] = useState(initialDraft);
  const [touched, setTouched] = useState(() => new Set());
  const [showAll, setShowAll] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [conflict, setConflict] = useState(null);
  const [impact, setImpact] = useState(null);
  const [testOpen, setTestOpen] = useState(false);
  const labels = adapter.labels || {};

  const patchDraft = useCallback((patch) => setDraft((d) => ({ ...d, ...patch })), []);
  const setProduct = useCallback((patch, opts) => {
    setDraft((d) => ({ ...d, product: { ...d.product, ...patch } }));
    if (opts?.silent) setBaseline((b) => ({ ...b, product: { ...b.product, ...patch } }));
  }, []);
  const data = useAdapterData(adapter, draft, setProduct);

  const allErrors = useMemo(() => validateDraft(adapter, draft, { data }), [adapter, draft, data]);
  const errors = useMemo(() => {
    if (showAll) return allErrors;
    return Object.fromEntries(Object.entries(allErrors).filter(([k]) => touched.has(k)));
  }, [allErrors, touched, showAll]);
  const touch = useCallback((key) => setTouched((t) => (t.has(key) ? t : new Set(t).add(key))), []);
  const errorCount = Object.keys(allErrors).length;

  // Unsaved changes: in-app navigation asks first, closing the tab warns.
  const dirty = JSON.stringify(draft) !== JSON.stringify(baseline);
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;
  const leavingRef = useRef(false);
  const blocker = useBlocker(
    useCallback(
      ({ currentLocation, nextLocation }) => dirtyRef.current && !leavingRef.current && currentLocation.pathname !== nextLocation.pathname,
      [],
    ),
  );
  const blockerRef = useRef(blocker);
  blockerRef.current = blocker;
  useEffect(() => {
    if (blocker.state !== "blocked") return;
    modalApi.confirm({
      title: "Leave without saving?",
      content: "Your changes to this policy will be lost.",
      okText: "Leave",
      cancelText: "Stay",
      okButtonProps: { danger: true },
      onOk: () => blockerRef.current.proceed && blockerRef.current.proceed(),
      onCancel: () => blockerRef.current.reset && blockerRef.current.reset(),
    });
  }, [blocker.state, modalApi]);
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const sectionProps = { draft, product: draft.product, setProduct, errors, touch, data, flags, isEdit };

  const send = (force = false) => {
    const policyJson = policyJsonFromDraft(adapter, draft, stored, { isEdit });
    const policyName = draft.policyName.trim();
    setSubmitting(true);
    setSaveError(null);
    const call = isEdit
      ? adapter.update({ id: adapter.rowId(row), policyName, policyJson, stored: row })
      : adapter.create({ policyName, policyJson, force });
    Promise.resolve(call)
      .then((res) => {
        leavingRef.current = true;
        // An update writes a new version: its new id is the policy from now on.
        const id = res?.id || (isEdit ? adapter.rowId(row) : null);
        const state = { toast: isEdit ? "Policy updated" : "Policy created", note: res?.warning || "" };
        if (id) navigate(policiesPath(product, encodeURIComponent(id)), { replace: true, state });
        else navigate(policiesPath(product), { replace: true, state });
      })
      .catch((f) => {
        setSubmitting(false);
        if (f?.kind === "conflict" && !isEdit) {
          setConflict(f);
          return;
        }
        // Never shown to the admin; kept for support.
        // eslint-disable-next-line no-console
        console.error("Policy save refused", f?.reason || "", f?.raw ?? f);
        if (f?.kind === "gone") setSaveError({ message: GONE_SAVE_ERROR });
        else if (f?.friendly) setSaveError({ message: f.friendly });
        else setSaveError(friendlyPolicyError(f?.reason));
      });
  };

  const save = () => {
    setShowAll(true);
    setConflict(null);
    if (errorCount) {
      scrollToError(firstErrorKey(allErrors));
      return;
    }
    send(false);
  };

  const cancel = () => navigate(isEdit && row ? policiesPath(product, encodeURIComponent(adapter.rowId(row))) : policiesPath(product));
  const title = `${isEdit ? "Edit" : "New"} ${labels.product} policy`;
  const Where = adapter.Where;
  const TestSignIn = adapter.features?.TestSignIn;
  const countImpact = () => {
    setImpact("Counting…");
    Promise.resolve(adapter.features.impact(draft, data))
      .then((text) => setImpact(text || "Couldn't count the matching users."))
      .catch(() => setImpact("Couldn't count the matching users."));
  };
  const conflictRow = (conflict?.conflicts || [])[0];
  const conflictId = conflictRow ? conflictRow.id ?? conflictRow.policyId ?? conflictRow.ID : null;
  const nameOk = !errors["basics.policyName"];
  const wide = useWide(TWO_COLUMNS);
  const toList = () => navigate(policiesPath(product));

  // The side panel: the policy as Who → Where → When → Then, and what's left.
  const sentence = policySummarySentence(adapter, draft, data);
  const subject = labels.subject || "sign-in";
  const everyone = !!(adapter.who?.everyone && draft.who?.everyone);
  const whoText = whoSentenceText({
    everyone,
    lists: (adapter.who?.fields || []).map((f) => ({ kind: f.kind || f.key, values: draft.who?.[f.key] || [] })),
  });
  const whereText = (adapter.sentenceWhere && adapter.sentenceWhere(draft, data)) || "";
  const clauses = conditionClauses(draft.conditions, { subject, product: adapter.key });
  const meta = actionMeta(draft.action);
  const flow = [
    { label: "Who", text: ucFirst(whoText), empty: whoText === "nobody yet" },
    { label: "Where", text: whereText ? ucFirst(whereText) : "Anywhere in scope", empty: !whereText },
    { label: "When", text: clauses.length ? ucFirst(clauses.join(", ")) : "Every time", empty: !clauses.length },
    { label: "Then", text: `${meta.label}${adapter.features?.fallback && draft.fallback ? " · fallback" : ""}`, color: meta.solid },
  ];
  const errorIn = (section) => Object.keys(allErrors).some((k) => sectionOf(k) === section);
  const checklist = [
    { key: "basics", label: "Name", ok: !errorIn("basics") },
    { key: "who", label: "Who", ok: !errorIn("who") },
    { key: "where", label: "Where", ok: !errorIn("where") },
    { key: "action", label: "What happens", ok: !errorIn("action") },
    ...(Object.keys(allErrors).some(isAdvancedKey) ? [{ key: "advanced", label: "Only when…", ok: false }] : []),
  ];
  const left = checklist.filter((c) => !c.ok).length;

  const saveNotes = (
    <>
      {saveError && <Note tone="danger">{saveError.message}</Note>}
      {conflict && (
        <Note
          tone="warn"
          role="alert"
          action={
            <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
              {conflictId != null && (
                <Button size="small" onClick={() => navigate(`${policiesPath(product, encodeURIComponent(conflictId))}/edit`)}>
                  Edit that policy
                </Button>
              )}
              <Button
                size="small"
                onClick={() => {
                  setConflict(null);
                  send(true);
                }}
              >
                Create anyway
              </Button>
            </div>
          }
        >
          {conflictRow?.policyName ? `This duplicates ${conflictRow.policyName}.` : "This duplicates another policy."}
        </Note>
      )}
    </>
  );

  const aside = (
    <aside aria-label="Policy preview" style={{ display: "grid", gap: "1rem", alignContent: "start", ...(wide ? { position: "sticky", top: "1rem" } : {}) }}>
      <Panel title="This policy will" style={{ borderTop: `3px solid ${meta.solid}` }}>
        <SummarySentence text={sentence} flow={draft.action} />
        <div style={{ borderTop: `1px solid ${AN_COLORS.line2}`, margin: "1rem 0", paddingTop: "1rem" }}>
          <FlowSteps steps={flow} />
        </div>
        {(adapter.features?.impact || TestSignIn) && (
          <div style={{ display: "grid", gap: "0.375rem" }}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "1rem" }}>
              {TestSignIn && <TextButton onClick={() => setTestOpen(true)}>Test a sign-in</TextButton>}
              {adapter.features?.impact && <TextButton onClick={countImpact}>Count matching users</TextButton>}
            </div>
            {impact && <Muted>{impact}</Muted>}
          </div>
        )}
      </Panel>

      <Panel label="Ready to save">
        <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: "0.5rem" }}>
          {checklist.map((c) => (
            <li key={c.key} style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontSize: "0.8125rem" }}>
              {c.ok ? (
                <span aria-hidden style={{ width: "1rem", height: "1rem", borderRadius: "50%", background: AN_COLORS.success, color: "#fff", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                  <Check size={11} strokeWidth={3} />
                </span>
              ) : (
                <Circle size={16} aria-hidden style={{ color: AN_COLORS.line }} />
              )}
              {c.ok ? (
                <span style={{ color: AN_COLORS.ink2 }}>{c.label}</span>
              ) : (
                <TextButton onClick={() => scrollToId(`policy-section-${c.key}`)} style={{ color: AN_COLORS.ink2, fontWeight: 400 }}>
                  {c.label}
                </TextButton>
              )}
            </li>
          ))}
        </ul>
        <div style={{ display: "grid", gap: "0.75rem", marginTop: "1rem" }}>
          {saveNotes}
          {showAll && errorCount > 0 ? (
            <span role="alert" style={{ color: AN_COLORS.danger, fontSize: "0.8125rem", fontWeight: 500 }}>
              {fixCountText(errorCount)}
            </span>
          ) : (
            left > 0 && <Muted>{left === 1 ? "1 thing left to fill in" : `${left} things left to fill in`}</Muted>
          )}
          <div style={{ display: "flex", gap: "0.5rem" }}>
            <Button onClick={cancel} disabled={submitting} style={{ flex: 1 }}>
              Cancel
            </Button>
            <Button type="primary" onClick={save} loading={submitting} disabled={!!conflict} style={{ flex: 2 }}>
              {isEdit ? "Update policy" : "Create policy"}
            </Button>
          </div>
        </div>
      </Panel>
    </aside>
  );

  const status = isEdit ? (
    <StatusDot tone={dirty ? "warn" : "success"}>{dirty ? "Unsaved changes" : "Saved · no changes yet"}</StatusDot>
  ) : (
    <StatusDot tone={dirty ? "warn" : "muted"}>{duplicate ? "Copy · not saved yet" : "Draft · not saved yet"}</StatusDot>
  );

  return (
    <PageShell>
      {modalContext}
      <div style={{ maxWidth: PAGE_MAX, width: "100%", marginInline: "auto", display: "grid", gap: "1.5rem" }}>
        <PageTitleBar
          onBack={toList}
          backLabel={`Back to ${labels.product} policies`}
          crumbs={[{ label: `${labels.product} policies`, onClick: toList }, { label: isEdit ? row?.policyName || "Policy" : "New policy" }]}
          title={title}
          status={status}
        />

        <div style={{ display: "grid", gridTemplateColumns: wide ? `minmax(0, 1fr) ${ASIDE_WIDTH}` : "minmax(0, 1fr)", gap: "1.5rem", alignItems: "start" }}>
          <div style={{ display: "grid", gap: "1rem", minWidth: 0 }}>
            <StepCard id="policy-section-basics" step={1} title="Name it" help="A short name your team will recognise in the list." done={!!draft.policyName.trim()} error={!nameOk}>
              <Field error={errors["basics.policyName"]} htmlFor={fieldDomId("basics.policyName")}>
                <Input
                  id={fieldDomId("basics.policyName")}
                  aria-label="Policy name"
                  size="large"
                  value={draft.policyName}
                  onChange={(e) => patchDraft({ policyName: e.target.value })}
                  onBlur={() => touch("basics.policyName")}
                  status={nameOk ? undefined : "error"}
                  placeholder="e.g. Domain admins need MFA"
                  maxLength={200}
                />
              </Field>
            </StepCard>
            <WhoSection adapter={adapter} draft={draft} patchDraft={patchDraft} errors={errors} touch={touch} data={data} />
            <StepCard
              id="policy-section-where"
              step={3}
              title="Where does it apply?"
              help={`The ${labels.product} resources this policy covers.`}
              done={!errorIn("where")}
              error={Object.keys(errors).some((k) => sectionOf(k) === "where")}
            >
              <Where {...sectionProps} />
            </StepCard>
            <ActionSection adapter={adapter} draft={draft} patchDraft={patchDraft} sectionProps={sectionProps} />
            <AdvancedBlock
              adapter={adapter}
              draft={draft}
              patchDraft={patchDraft}
              errors={errors}
              allErrors={allErrors}
              touch={touch}
              data={data}
              warnings={conditionWarnings(adapter, draft)}
            />
          </div>
          {aside}
        </div>
      </div>

      {TestSignIn && (
        <Drawer title="Test a sign-in" open={testOpen} onClose={() => setTestOpen(false)} width="28rem">
          <TestSignIn draft={draft} data={data} />
        </Drawer>
      )}
    </PageShell>
  );
}
